import { ref } from 'vue'
import apiClient from '../api/axios.js'
import { buildComboSendPayload } from '../utils/sendData.js'

// 组合动作的顺序执行与进度管理
// 外部依赖：
//   deviceId     - 当前设备ID (Ref)
//   selectedEnv  - 当前环境对象 (Ref，需含 manager_url)
//   showSnackbar - 显示提示信息的回调 (msg: string) => void
export function useCombo({ deviceId, selectedEnv, showSnackbar }) {
  const comboRunning = ref(false)
  const comboTotal = ref(0)
  const comboCurrentIndex = ref(0)
  const comboCurrentName = ref('')
  const comboStatusMsg = ref('')
  const comboStopRequested = ref(false)

  // 简单延时工具（可被停止）
  function comboSleep(ms) {
    return new Promise(resolve => {
      if (ms <= 0) { resolve(); return }
      const timer = setTimeout(resolve, ms)
      // 停止时立即结束等待
      const checkStop = setInterval(() => {
        if (comboStopRequested.value) {
          clearTimeout(timer)
          clearInterval(checkStop)
          resolve()
        }
      }, 100)
      setTimeout(() => clearInterval(checkStop), ms + 50)
    })
  }

  // 停止组合动作
  function stopCombo() {
    comboStopRequested.value = true
  }

  // 顺序执行组合动作；批量测试时会把每个动作同时下发到所有目标设备，并支持整组循环。
  async function handleRunCombo(payload, executionOptions = {}) {
    const items = (payload && payload.items) || []
    if (items.length === 0) return

    const configuredDeviceIds = Array.isArray(executionOptions.deviceIds)
      ? executionOptions.deviceIds.filter(Boolean)
      : []
    const targetDeviceIds = [...new Set(
      configuredDeviceIds.length > 0 ? configuredDeviceIds : [deviceId.value].filter(Boolean)
    )]
    const loopCount = Math.max(1, Math.floor(Number(executionOptions.loopCount) || 1))
    const isBatchExecution = configuredDeviceIds.length > 0

    if (targetDeviceIds.length === 0) {
      showSnackbar('请先选择设备ID')
      return
    }
    if (!selectedEnv.value || !selectedEnv.value.manager_url) {
      showSnackbar('请先选择环境')
      return
    }

    comboStopRequested.value = false
    comboRunning.value = true
    comboTotal.value = items.length * loopCount
    comboCurrentIndex.value = 0
    comboCurrentName.value = ''
    comboStatusMsg.value = ''

    let executed = 0
    let skipped = 0
    let failed = 0

    try {
      outer:
      for (let loopIndex = 0; loopIndex < loopCount; loopIndex++) {
        for (let i = 0; i < items.length; i++) {
          if (comboStopRequested.value) break outer
          const item = items[i]
          const loopLabel = loopCount > 1 ? `第 ${loopIndex + 1}/${loopCount} 轮 · ` : ''
          comboCurrentName.value = `${loopLabel}${i + 1}. ${item.operationName}`

          const sendObjects = targetDeviceIds
            .map(targetDeviceId => buildComboSendPayload(item.mcpData, targetDeviceId))
            .filter(Boolean)
          if (sendObjects.length === 0) {
            skipped++
            comboStatusMsg.value = `已跳过「${item.operationName}」（无动作数据）`
            comboCurrentIndex.value = loopIndex * items.length + i + 1
            continue
          }

          const results = await Promise.allSettled(
            sendObjects.map(sendObj => apiClient.post(
              selectedEnv.value.manager_url + '/xiaozhi/device/send-message',
              sendObj,
              { headers: { 'Content-Type': 'application/json' } }
            ))
          )
          const successCount = results.filter(result => result.status === 'fulfilled').length
          const failedCount = results.length - successCount
          executed += successCount
          failed += failedCount

          if (failedCount > 0) {
            results
              .filter(result => result.status === 'rejected')
              .forEach(result => console.error('组合动作下发失败:', result.reason))
            comboStatusMsg.value = `「${item.operationName}」已下发 ${successCount}/${results.length} 台设备`
          } else if (isBatchExecution) {
            comboStatusMsg.value = `「${item.operationName}」已下发至 ${successCount} 台设备`
          }

          comboCurrentIndex.value = loopIndex * items.length + i + 1

          // 等待当前动作播放结束 + 设定的间隔，再执行下一个动作或下一轮。
          const hasNext = i < items.length - 1 || loopIndex < loopCount - 1
          if (hasNext && !comboStopRequested.value) {
            const wait = (item.durationMs || 0) + (item.intervalMs || 0)
            comboStatusMsg.value = `等待 ${wait} ms 后继续执行...`
            await comboSleep(wait)
          }
        }
      }

      if (comboStopRequested.value) {
        showSnackbar(`组合表情已停止（成功下发 ${executed} 次）`)
      } else if (isBatchExecution) {
        showSnackbar(
          `批量组合表情完成：${targetDeviceIds.length} 台设备，${loopCount} 轮，成功 ${executed} 次`
          + (failed > 0 ? `，失败 ${failed} 次` : '')
          + (skipped > 0 ? `，跳过 ${skipped} 个动作` : '')
        )
      } else {
        showSnackbar(skipped > 0
          ? `组合动作执行完成：成功 ${executed} 个，跳过 ${skipped} 个`
          : `组合动作执行完成（共 ${executed} 个）`)
      }
    } catch (err) {
      console.error('组合动作执行异常:', err)
      showSnackbar('组合动作执行异常')
    } finally {
      comboRunning.value = false
      comboStopRequested.value = false
    }
  }

  return {
    comboRunning,
    comboTotal,
    comboCurrentIndex,
    comboCurrentName,
    comboStatusMsg,
    stopCombo,
    handleRunCombo
  }
}
